import { useTranslation } from '@/hooks/use-translation';
import { motion } from 'motion/react';

interface YearData {
  year: string;
  label: string;
  clients: string;
  clientTypes: string;
  acv: string;
  arr: string;
  focus: string;
}

interface Assumption {
  title: string;
  description: string;
}

export function RevenueForecast() {
  const { t, tArray } = useTranslation();
  const years = tArray<YearData>('revenueForecast.years');
  const assumptions = tArray<Assumption>('revenueForecast.assumptions');

  return (
    <section id="revenue" className="bg-slate-100 dark:bg-[#0a0f14] py-20 lg:py-28 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-5">
        <svg viewBox="0 0 400 600" className="w-full h-full">
          <path d="M0 0 L400 200 L400 600 L0 400 Z" fill="#7ab8c9" />
        </svg>
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block mb-4 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.2em] uppercase font-medium">
            {t('revenueForecast.label')}
          </span>
          <h2 className="font-display text-slate-900 dark:text-[#e8ebe9] text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl tracking-tight">
            {t('revenueForecast.title')}
          </h2>
          <p className="text-slate-600 dark:text-[#8a9a9e] text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
            {t('revenueForecast.description')}
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-linear-to-r from-slate-200 dark:from-[#1a2530] via-slate-400 dark:via-[#3d5a6e] to-slate-200 dark:to-[#1a2530]" />

          <div className="grid lg:grid-cols-3 gap-8">
            {years.map((item, index) => (
              <motion.div
                key={item.year}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <div className="p-8 lg:p-10 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white/80 dark:bg-[#0d1318]/50 backdrop-blur-sm relative transition-all duration-300 hover:shadow-lg hover:border-slate-300 dark:hover:border-[#3d5a6e]">
                  {/* Year badge */}
                  <div className="absolute -top-4 left-8 px-4 py-1.5 rounded-lg bg-slate-50 dark:bg-[#0a0f14] border border-slate-300 dark:border-[#3d5a6e]">
                    <span className="text-[#3d6a7a] dark:text-[#7ab8c9] text-sm font-medium">
                      {item.year}
                    </span>
                  </div>

                  <div className="mt-4">
                    <span className="text-[#3d6a7a] dark:text-[#7ab8c9] text-xs md:text-sm tracking-wider uppercase font-medium">
                      {item.label}
                    </span>

                    {/* ARR highlight */}
                    <div className="mt-6 mb-6 py-4 border-y border-slate-200 dark:border-[#1a2530]">
                      <span className="text-slate-500 dark:text-[#6a7a7e] text-xs md:text-sm block mb-1">
                        {t('revenueForecast.arrLabel')}
                      </span>
                      <span className="font-display text-slate-900 dark:text-[#e8ebe9] text-2xl md:text-3xl">
                        {item.arr}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-[#6a7a7e] text-sm">
                          {t('revenueForecast.clientsLabel')}
                        </span>
                        <span className="text-slate-700 dark:text-[#c8d0d2] text-sm font-medium">
                          {item.clients}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-[#6a7a7e] text-sm">
                          {t('revenueForecast.avgContractLabel')}
                        </span>
                        <span className="text-slate-700 dark:text-[#c8d0d2] text-sm font-medium">
                          {item.acv}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-[#6a7a7e] text-sm">
                          {t('revenueForecast.mixLabel')}
                        </span>
                        <span className="text-slate-700 dark:text-[#c8d0d2] text-sm text-right font-medium">
                          {item.clientTypes}
                        </span>
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-slate-200 dark:border-[#1a2530]">
                      <span className="text-[#3d6a7a] dark:text-[#7ab8c9] text-xs md:text-sm font-medium">
                        {item.focus}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Assumptions note */}
        <motion.div
          className="mt-16 grid md:grid-cols-3 gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {assumptions.map((assumption) => (
            <div
              key={assumption.title}
              className="p-4 lg:p-5 rounded-lg border-l-2 border-[#3d6a7a] dark:border-[#7ab8c9] bg-white/50 dark:bg-transparent"
            >
              <span className="text-slate-500 dark:text-[#6a7a7e] text-xs md:text-sm block mb-1 font-medium">
                {assumption.title}
              </span>
              <span className="text-slate-700 dark:text-[#c8d0d2] text-sm md:text-base">
                {assumption.description}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
